import { APIResponse } from '../types/api_response.model';
import { AIDataModel } from '@manifest-cyber/types/interface/aisbom';
import { AxiosProxy } from './axiosProxy/axiosProxy';
import { buildUrlWithParams } from '../lib/url/buildWithParams/buildWithParams';
import { mapModelsResponse } from './models.mapper';

export interface Dataset {
  title: string;
  categories: string[];
  purl: string;
  purlWithoutRevision: string;
  homepageUrl: string;
  supplier: string;
  supplierCountryOfOrigin: string[];
  description: string;
  licenses: License[];
  researchPapers: ResearchPaper[];
  lastKnownDownloadCount: number;
  lastKnownLikedCount: number;
  numRows: number;
  numBytesOriginalFiles: number;
  numBytesMemory: number;
  paperWithCodeId: string;
  dateCreated: string;
  dateModified: string;
}

export interface ResearchPaper {
  title?: string;
  url?: string;
  authors?: string[];
  publishedDate?: string;
  abstract?: string;
}

export interface License {
  rawLicenseId?: string;
  licenseId?: string | null;
  name?: string;
  url?: string;
}

export interface Note {
  id?: string;
  content?: string;
  author?: string;
  dateCreated?: string;
  dateModified?: string;
}

// Extend the existing AIDataModel from the types library with our specific API response fields
export interface Model extends Partial<AIDataModel> {
  _id: string;
  purl: string;
  organizationId: string;
  __v: number;
  baseModelPurl: string | null;
  categories: string[];
  datasets: Dataset[];
  dateCreated: string;
  dateModified: string;
  homepageUrl: string;
  lastKnownDownloadCount: number;
  lastKnownLikedCount: number;
  lastModified: string;
  licenses: License[];
  name: string;
  notes: Note[];
  purlWithoutRevision: string;
  researchPapers: ResearchPaper[];
  supplier: string;
  supplierCountryOfOrigin: string[];
  version?: string;
}

export interface ModelsApiResponse {
  modelsByAsset: Record<string, Model[]>;
  total: number;
  page: number;
  limit: number;
}

export interface TransformedModelsResponse {
  data: Model[];
  success: boolean;
  total: number;
  page: number;
  limit: number;
}

export type FetchModelsParams = {
  assetId: string;
  page?: number;
  limit?: number;
};

export const getModels = async (
  params: FetchModelsParams,
): Promise<TransformedModelsResponse> => {
  const { assetId, page, limit } = params;
  const url = buildUrlWithParams('/models', {
    assetId,
    page,
    limit,
  });

  const response = (await AxiosProxy.get({
    url,
    withCredentials: true,
  })) as APIResponse<ModelsApiResponse>;

  return mapModelsResponse(response, assetId);
};
